package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Crop
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.ZoomIn
import androidx.compose.material.icons.filled.ZoomOut
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.ClipOp
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.FilterQuality
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.clipPath
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.min
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.photo.CropMath
import com.evoapps.evoshift.photo.CropTransform
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.profile_photo_crop_area
import evoshift.composeapp.generated.resources.profile_photo_crop_hint
import evoshift.composeapp.generated.resources.profile_photo_crop_title
import evoshift.composeapp.generated.resources.profile_photo_reset
import evoshift.composeapp.generated.resources.profile_photo_zoom_in
import evoshift.composeapp.generated.resources.profile_photo_zoom_out
import evoshift.composeapp.generated.resources.save
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

object ProfilePhotoCropTags {
    const val AREA = "profile_photo_crop_area"
}

/**
 * "Crop your photo" (SH-94): the picked photo under a circle in a square window. Drag (finger
 * or mouse) moves it, pinch or the mouse wheel zooms around the fingers / pointer, double-tap
 * starts again; the buttons do the same for keyboards and screen readers. The photo always
 * fills the circle (CropMath). Save hands over the square the window shows, in image pixels.
 */
@Composable
fun ProfilePhotoCropDialog(
    image: ImageBitmap,
    onSave: (IntRect) -> Unit,
    onCancel: () -> Unit
) {
    var viewport by remember { mutableStateOf(0f) }
    val math = remember(image, viewport) { if (viewport > 0f) CropMath(image.width, image.height, viewport) else null }
    var transform by remember(math) { mutableStateOf(math?.initial()) }

    EvoDialog(
        title = stringResource(Res.string.profile_photo_crop_title),
        icon = Icons.Default.Crop,
        onDismiss = onCancel,
        width = DialogWidth.Compact,
        dismissOnClickOutside = false,
        confirm = DialogAction(
            stringResource(Res.string.save),
            { math?.let { m -> transform?.let { onSave(m.cropRect(it)) } } },
            enabled = math != null
        )
    ) {
        Text(stringResource(Res.string.profile_photo_crop_hint), color = Color.White.copy(alpha = 0.85f))
        BoxWithConstraints(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
            val side = min(maxWidth, 360.dp)
            val sidePx = with(LocalDensity.current) { side.toPx() }
            SideEffect { if (viewport != sidePx) viewport = sidePx }
            CropArea(
                image = image,
                math = math,
                transform = transform,
                onTransform = { transform = it },
                modifier = Modifier.size(side)
            )
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.CenterVertically) {
            val zoomOut = stringResource(Res.string.profile_photo_zoom_out)
            val zoomIn = stringResource(Res.string.profile_photo_zoom_in)
            val reset = stringResource(Res.string.profile_photo_reset)
            IconButtonWithTooltip(onClick = { math?.let { m -> transform = transform?.let { m.zoom(it, 1f / CropMath.ZOOM_STEP) } } }, enabled = math != null, tooltip = zoomOut) {
                Icon(Icons.Default.ZoomOut, contentDescription = zoomOut, tint = Color.White)
            }
            IconButtonWithTooltip(onClick = { math?.let { transform = it.initial() } }, enabled = math != null, tooltip = reset) {
                Icon(Icons.Default.Refresh, contentDescription = reset, tint = Color.White)
            }
            IconButtonWithTooltip(onClick = { math?.let { m -> transform = transform?.let { m.zoom(it, CropMath.ZOOM_STEP) } } }, enabled = math != null, tooltip = zoomIn) {
                Icon(Icons.Default.ZoomIn, contentDescription = zoomIn, tint = Color.White)
            }
        }
    }
}

@Composable
private fun CropArea(
    image: ImageBitmap,
    math: CropMath?,
    transform: CropTransform?,
    onTransform: (CropTransform) -> Unit,
    modifier: Modifier
) {
    val description = stringResource(Res.string.profile_photo_crop_area)
    // The gesture handlers outlive recompositions: they read the latest transform through this.
    val current = remember { CurrentTransform() }
    current.value = transform
    Box(
        modifier
            .clipToBounds()
            .testTag(ProfilePhotoCropTags.AREA)
            .semantics { contentDescription = description }
            .pointerInput(math) {
                val m = math ?: return@pointerInput
                detectTransformGestures { centroid, pan, zoom, _ ->
                    val start = current.value ?: return@detectTransformGestures
                    val centre = Offset(size.width / 2f, size.height / 2f)
                    val next = m.pan(m.zoom(start, zoom, centroid - centre), pan)
                    current.value = next
                    onTransform(next)
                }
            }
            .pointerInput(math) {
                val m = math ?: return@pointerInput
                detectTapGestures(onDoubleTap = { m.initial().also { current.value = it; onTransform(it) } })
            }
            .pointerInput(math) {
                val m = math ?: return@pointerInput
                // The mouse wheel (web, desktop): one notch is one zoom step around the pointer.
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        if (event.type != PointerEventType.Scroll) continue
                        val change = event.changes.firstOrNull() ?: continue
                        val dy = change.scrollDelta.y
                        val start = current.value ?: continue
                        if (dy == 0f) continue
                        val centre = Offset(size.width / 2f, size.height / 2f)
                        val next = m.zoom(start, if (dy < 0f) CropMath.ZOOM_STEP else 1f / CropMath.ZOOM_STEP, change.position - centre)
                        current.value = next
                        onTransform(next)
                        change.consume()
                    }
                }
            }
    ) {
        Canvas(Modifier.fillMaxSize()) {
            val t = transform ?: return@Canvas
            val width = image.width * t.scale
            val height = image.height * t.scale
            val left = size.width / 2f + t.offset.x - width / 2f
            val top = size.height / 2f + t.offset.y - height / 2f
            drawImage(
                image,
                srcOffset = IntOffset.Zero,
                srcSize = IntSize(image.width, image.height),
                dstOffset = IntOffset(left.roundToInt(), top.roundToInt()),
                dstSize = IntSize(width.roundToInt().coerceAtLeast(1), height.roundToInt().coerceAtLeast(1)),
                filterQuality = FilterQuality.Medium
            )
            // Outside the circle is dimmed: what shows in the avatar is what is in the circle.
            val circle = Path().apply { addOval(Rect(Offset.Zero, size)) }
            clipPath(circle, clipOp = ClipOp.Difference) { drawRect(Color.Black.copy(alpha = 0.6f)) }
            val stroke = 2.dp.toPx()
            drawCircle(Color.White.copy(alpha = 0.9f), radius = size.minDimension / 2f - stroke / 2f, style = Stroke(stroke))
        }
    }
}

private class CurrentTransform {
    var value: CropTransform? = null
}
